import React, { useEffect, useMemo, useState } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  IconButton,
  ListItemIcon,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../../utils/dataTableShell";
import { AxiosResponse } from "axios";
import StatusBadge from "../../Common/UI/StatusTag";
import AddInstrumentModal from "./AddInstrumentModal";
import {
  CloseRounded,
  FilterAlt,
  MoreVert,
  RadioButtonChecked,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { artifactsLabelMap, artifactsTypes } from "../../../utils/artifacts";
import { GridSearchIcon } from "@mui/x-data-grid";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import { v4 as uuid } from "uuid";

type Props = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const statusMap: any = {
  active: { label: "Active", variant: "active" },
  inactive: { label: "Inactive", variant: "cancelled" },
};

const InstrumentsList = ({ showModal, setShowModal }: Props) => {
  const [loading, setLoading] = useState(true);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [instruments, setInstruments] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [instrumentsCount, setInstrumentsCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [filterEl, setFilterEl] = useState<null | HTMLElement>(null);
  const openFilter = Boolean(filterEl);

  const [filter, setFilter] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get("query") ?? "");
  const [searchId, setSearchId] = useState(uuid());

  useEffect(() => {
    const params = new URLSearchParams();

    if (searchText) {
      params.set("query", searchText);
    }

    setSearchParams(params, {
      replace: true,
    });
  }, [setSearchParams, searchText]);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        setLoading(true);
        let url = `/instrument-library?page=${page + 1}&size=${pageSize}`;

        if (filter) {
          url += `&type=${filter}`;
        }

        if (searchText) {
          url += `&title=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.rows;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          title: item?.title || "",
          // diseaseArea: item?.diseaseArea || "",
          // source: item?.source || "",
          // cost: item?.cost || "",
          fileUrl: item?.fileUrl || "",
          status: item?.status || "",
          description: item?.description || "",
          type: item?.type,
          typeLabel: artifactsLabelMap[item?.type] || "",
        }));

        setInstruments(newData || []);
        setInstrumentsCount(res.data?.data?.count || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchInstruments();
  }, [toggleLoader, page, filter, searchText]);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterEl(event.currentTarget);
  };

  const closeFilterMenu = () => {
    setFilterEl(null);
  };

  const openModal = () => {
    setShowModal(true);
    setAnchorEl(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const updateStatus = async (status: string) => {
    try {
      setMenuLoader(true);
      const res: AxiosResponse = await http.patch(
        `/instrument-library/${selectedRow?.id}/status`,
        {
          status,
        }
      );
      toastMessage("success", res.data.message);
      handleMenuClose();
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setMenuLoader(false);
    }
  };

  const handleFilterChange = (val: string) => {
    setFilter(val);
    setPage(0);
    closeFilterMenu();
  };

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setPage(0);
        setSearchText(val);
      }, 500),
    [setSearchText]
  );
  const handleClearSearch = () => {
    setSearchText("");
    setSearchId(uuid());
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <TextField
              key={searchId}
              placeholder="Search"
              sx={{ width: "30%" }}
              defaultValue={searchText}
              onChange={(e) => modifySearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch}>
                      <CloseRounded />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <IconButton
                onClick={openFilterMenu}
                sx={{
                  height: "48px",
                  width: "48px",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                }}
              >
                <FilterAlt
                  color={filter ? "primary" : "disabled"}
                  sx={{ fontSize: "24px" }}
                />
              </IconButton>
            </Box>
          </Box>
        }
        footer={
          <DataTableFooter
            filteredCount={instrumentsCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
        <TableHead>
          <TableRow>
            <StyledTableCell>Title</StyledTableCell>
            {/* <StyledTableCell>Disease Area</StyledTableCell>
            <StyledTableCell>Source / Location</StyledTableCell>
            <StyledTableCell>Approximate Cost</StyledTableCell> */}
            <StyledTableCell>Type</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell />
          </TableRow>
        </TableHead>
        {!loading && instruments?.length > 0 && (
          <TableBody>
            {instruments?.map((row: any) => (
              <TableRow key={row?.id}>
                <StyledTableCell>{row?.title || "-"}</StyledTableCell>
                <StyledTableCell>{row?.typeLabel || "-"}</StyledTableCell>
                <StyledTableCell>
                  <StatusBadge
                    label={statusMap[row?.status]?.label ?? row?.status}
                    variant={statusMap[row?.status]?.variant}
                  />
                </StyledTableCell>
                <StyledTableCell align="right">
                  <IconButton onClick={(e) => handleMenuClick(e, row)}>
                    <MoreVert />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
        </Table>
        {!loading && instruments.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
        TransitionProps={{
          exit: false,
        }}
      >
        <MenuItem disabled={menuLoader} onClick={openModal}>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={menuLoader}
          onClick={() =>
            updateStatus(
              selectedRow?.status === "active" ? "inactive" : "active"
            )
          }
        >
          <ListItemText>
            {selectedRow?.status === "active" ? "De-activate" : "Activate"}
          </ListItemText>
          {menuLoader && (
            <ListItemIcon>
              <CircularProgress size={18} sx={{ ml: 1 }} />
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={filterEl}
        open={openFilter}
        onClose={closeFilterMenu}
        sx={{
          "& .MuiPaper-root": {
            width: 200,
          },
        }}
      >
        {artifactsTypes.map((item) => (
          <MenuItem key={item.key} onClick={() => handleFilterChange(item.key)}>
            <ListItemIcon>
              {item.key === filter ? (
                <RadioButtonChecked />
              ) : (
                <RadioButtonUnchecked />
              )}
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => handleFilterChange("")}>Clear</MenuItem>
      </Menu>

      {showModal && (
        <AddInstrumentModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default InstrumentsList;
